import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import {
  DeriveTrackerInput,
  TRACKER_STEP_ORDER,
  buildMinimalTracker,
} from "./trackerSteps";
import { TRACKER_STEP_ICONS } from "./trackerIcons";
import {
  Dot,
  DotRow,
  MinimalRow,
  OrderStatusHeaderRow,
  Segment,
  SegmentBar,
  TrackerContainer,
  TrackerDate,
  TrackerHeaderLabel,
  TrackerHeaderStatic,
  TrackerIcon,
  TrackerLabel,
  TrackerSubtext,
  TrackerTitle,
} from "./OrderStatusTracker.styles";

interface MinimalTrackerProps {
  input: DeriveTrackerInput;
  showEstimatedDate: boolean;
  /** Section heading shown above the tracker (e.g. "Aneuploidy Screen"). */
  title: string;
}

/**
 * Minimal "Order status" tracker. Two visual treatments, keyed off the
 * experiment sub-variant:
 * - Show-date (C1): a compact 4-dot progress indicator next to "Order status",
 *   plus the estimated-results-date line under the current step.
 * - Hide-date (C2, "Pizza Tracker"): a horizontal segmented progress bar at the
 *   bottom of the section, with no estimated-results-date line.
 * Both show the current step's icon, label and event date.
 */
export const MinimalTracker: React.FC<MinimalTrackerProps> = ({
  input,
  showEstimatedDate,
  title,
}) => {
  const view = buildMinimalTracker(input, showEstimatedDate);
  const icon = TRACKER_STEP_ICONS[view.currentStepId];
  // Each segment maps 1:1 to a tracker step, so key off the stable step id
  // rather than the array index.
  const segments = TRACKER_STEP_ORDER.map((stepId, index) => ({
    stepId,
    filled: index < view.filledSegments,
  }));

  return (
    <TrackerContainer data-testid="minimal-tracker">
      <TrackerTitle>{title}</TrackerTitle>
      {showEstimatedDate ? (
        <OrderStatusHeaderRow>
          <TrackerHeaderLabel>Order status</TrackerHeaderLabel>
          <DotRow aria-hidden>
            {segments.map(({ stepId, filled }) => (
              <Dot key={stepId} filled={filled}>
                <FontAwesomeIcon icon={filled ? faCircleCheck : faCircle} />
              </Dot>
            ))}
          </DotRow>
        </OrderStatusHeaderRow>
      ) : (
        <TrackerHeaderStatic>
          <TrackerHeaderLabel>Order status</TrackerHeaderLabel>
        </TrackerHeaderStatic>
      )}
      <MinimalRow>
        <TrackerDate accent="neutral">{view.date}</TrackerDate>
        <TrackerIcon active accent="neutral" $leftAlign>
          <FontAwesomeIcon aria-hidden icon={icon} />
        </TrackerIcon>
        <span>
          <TrackerLabel active accent="neutral">
            {view.label}
          </TrackerLabel>
          {view.subtext && <TrackerSubtext>{view.subtext}</TrackerSubtext>}
        </span>
      </MinimalRow>
      {!showEstimatedDate && (
        <SegmentBar aria-hidden>
          {segments.map(({ stepId, filled }) => (
            <Segment key={stepId} filled={filled} />
          ))}
        </SegmentBar>
      )}
    </TrackerContainer>
  );
};
